import React, { useState, useEffect } from "react";
import { Box, Drawer, Chip, IconButton } from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { useTasksTab } from "../../hooks/Tasks/useTasksTab";
import { TASK_TEMPLATES, PRIMARY_COLOR } from "./constants";
import { TaskTemplate } from "./types";
import { tokens } from "../../theme/tokens";
import SubjectTypeToggle from "../shared/SubjectTypeToggle";
import { SubjectType } from "../../types/CaseTypes";

const TEMPLATE_ICONS: Record<string, React.ElementType> = {
  "Phone Lookup": PhoneOutlinedIcon,
  "Email Search": EmailOutlinedIcon,
  "Address Check": HomeOutlinedIcon,
  "Social Media": LanguageOutlinedIcon,
  "Internet Search": LanguageOutlinedIcon,
  "Data Provider": StorageOutlinedIcon,
  Associates: PeopleOutlinedIcon,
  Relatives: PeopleOutlinedIcon,
  "Field Resources": ExploreOutlinedIcon,
  "Records Request": FolderOutlinedIcon,
  "Custom Task": EditOutlinedIcon,
};

const NewTaskSheet: React.FC = () => {
  const {
    newTaskSheetOpen,
    closeNewTaskSheet,
    createTask,
    pois,
    activePoiId,
    createPoi,
  } = useTasksTab();

  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(
    null,
  );
  const defaultPoiId = () =>
    activePoiId ?? pois.find((p) => p.is_primary)?.id ?? pois[0]?.id ?? '';

  const [selectedPoiId, setSelectedPoiId] = useState<string>(defaultPoiId);
  const [customTitle, setCustomTitle] = useState("");
  const [addingPoi, setAddingPoi] = useState(false);
  const [newPoiFirst, setNewPoiFirst] = useState("");
  const [newPoiLast, setNewPoiLast] = useState("");
  const [newPoiType, setNewPoiType] = useState<SubjectType>("person");
  const isBusinessPoi = newPoiType === "business";

  // Keep selectedPoiId in sync when activePoiId changes (e.g. after creating a new POI)
  useEffect(() => {
    setSelectedPoiId(activePoiId ?? pois.find((p) => p.is_primary)?.id ?? pois[0]?.id ?? '');
  }, [activePoiId, pois]);

  const handleConfirmNewPoi = () => {
    if (!newPoiFirst.trim() || (!isBusinessPoi && !newPoiLast.trim())) return;
    createPoi(newPoiFirst, isBusinessPoi ? "" : newPoiLast, newPoiType);
    setAddingPoi(false);
    setNewPoiFirst("");
    setNewPoiLast("");
    setNewPoiType("person");
    // activePoiId will update via context — selectedPoiId will sync on next render
  };

  const handleClose = () => {
    closeNewTaskSheet();
    setSelectedTemplate(null);
    setCustomTitle("");
  };

  const handleCreate = () => {
    if (!selectedTemplate) return;
    createTask(
      selectedTemplate,
      selectedPoiId,
      selectedTemplate.task_type === "CUSTOM"
        ? customTitle || "Custom Task"
        : undefined,
    );
    setSelectedTemplate(null);
    setCustomTitle("");
  };

  const isReady = !!selectedTemplate && !!selectedPoiId;

  return (
    <Drawer
      anchor="bottom"
      open={newTaskSheetOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "16px 16px 0 0",
          maxHeight: "65vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Handle bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: "10px",
          pb: "4px",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            backgroundColor: tokens.color.border.default,
          }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: "20px",
          pb: "12px",
          pt: "4px",
          borderBottom: `1px solid ${tokens.color.border.default}`,
        }}
      >
        <span
          style={{
            fontSize: "15px",
            fontWeight: 600,
            color: tokens.color.text.primary,
            flex: 1,
          }}
        >
          New Task
        </span>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: tokens.color.text.secondary }}
        >
          <CloseIcon sx={{ fontSize: "18px" }} />
        </IconButton>
      </Box>

      <Box sx={{ overflowY: "auto", px: "20px", py: "14px", flex: 1 }}>
        {/* POI selector */}
        <Box sx={{ mb: "16px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: tokens.color.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            POI
          </span>
          <Box
            sx={{
              display: "flex",
              gap: "6px",
              mt: "6px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {pois.map((poi) => (
              <Chip
                key={poi.id}
                label={poi.is_primary ? `${poi.name} ★` : poi.name}
                size="small"
                onClick={() => setSelectedPoiId(poi.id)}
                sx={{
                  fontSize: "12px",
                  height: "26px",
                  borderRadius: "6px",
                  fontWeight: selectedPoiId === poi.id ? 600 : 400,
                  backgroundColor:
                    selectedPoiId === poi.id ? tokens.color.text.primary : tokens.color.surface.subtle,
                  color: selectedPoiId === poi.id ? tokens.color.surface.default : tokens.color.text.secondary,
                  border:
                    selectedPoiId === poi.id
                      ? `1px solid ${tokens.color.text.primary}`
                      : `1px solid ${tokens.color.border.default}`,
                  cursor: "pointer",
                  "& .MuiChip-label": { px: "10px" },
                }}
              />
            ))}
            {!addingPoi && (
              <button
                type="button"
                onClick={() => setAddingPoi(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: tokens.color.text.secondary,
                  backgroundColor: "transparent",
                  border: `1px dashed ${tokens.color.border.strong}`,
                  borderRadius: "6px",
                  padding: "3px 10px",
                  cursor: "pointer",
                  height: "26px",
                }}
              >
                <PersonAddOutlinedIcon style={{ fontSize: "13px" }} />
                New POI
              </button>
            )}
          </Box>

          {/* Inline new-POI fields */}
          {addingPoi && (
            <>
              <Box sx={{ mt: tokens.space[2] }}>
                <SubjectTypeToggle value={newPoiType} onChange={setNewPoiType} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  mt: "8px",
                  alignItems: "center",
                }}
              >
                <input
                  autoFocus
                  value={newPoiFirst}
                  onChange={(e) => setNewPoiFirst(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleConfirmNewPoi();
                  }}
                  placeholder={isBusinessPoi ? "Business name" : "First name"}
                  aria-label={isBusinessPoi ? "Business name" : "First name"}
                  style={{
                    flex: 1,
                    fontSize: "12px",
                    padding: "6px 8px",
                    border: `1px solid ${tokens.color.border.strong}`,
                    borderRadius: "6px",
                    outline: "none",
                    color: tokens.color.text.primary,
                  }}
                />
                {!isBusinessPoi && (
                  <input
                    value={newPoiLast}
                    onChange={(e) => setNewPoiLast(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleConfirmNewPoi();
                    }}
                    placeholder="Last name"
                    aria-label="Last name"
                    style={{
                      flex: 1,
                      fontSize: "12px",
                      padding: "6px 8px",
                      border: `1px solid ${tokens.color.border.strong}`,
                      borderRadius: "6px",
                      outline: "none",
                      color: tokens.color.text.primary,
                    }}
                  />
                )}
                <button
                  type="button"
                  onClick={handleConfirmNewPoi}
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: tokens.color.surface.default,
                    backgroundColor: tokens.color.text.primary,
                    border: "none",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddingPoi(false);
                    setNewPoiFirst("");
                    setNewPoiLast("");
                    setNewPoiType("person");
                  }}
                  style={{
                    fontSize: "12px",
                    color: tokens.color.text.secondary,
                    backgroundColor: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "6px 4px",
                  }}
                >
                  ✕
                </button>
              </Box>
            </>
          )}
        </Box>

        {/* Task type grid */}
        <Box sx={{ mb: "4px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: tokens.color.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Task Type
          </span>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "8px",
              mt: "8px",
            }}
          >
            {TASK_TEMPLATES.map((template) => {
              const Icon = TEMPLATE_ICONS[template.label] ?? EditOutlinedIcon;
              const isSelected = selectedTemplate?.label === template.label;
              return (
                <button
                  type="button"
                  key={template.label}
                  onClick={() => setSelectedTemplate(template)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    padding: "10px 6px",
                    borderRadius: "9px",
                    border: isSelected
                      ? `2px solid ${PRIMARY_COLOR}`
                      : `1px solid ${tokens.color.border.default}`,
                    backgroundColor: isSelected
                      ? tokens.color.surface.hoverStrong
                      : tokens.color.surface.default,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    minHeight: "72px",
                  }}
                >
                  <Icon
                    style={{
                      fontSize: "20px",
                      color: isSelected ? PRIMARY_COLOR : tokens.color.text.secondary,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? PRIMARY_COLOR : tokens.color.text.primary,
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {template.label}
                  </span>
                </button>
              );
            })}
          </Box>
        </Box>

        {/* Custom task fields */}
        {selectedTemplate?.task_type === "CUSTOM" && (
          <Box sx={{ mt: "14px" }}>
            <label
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: tokens.color.text.secondary,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Title
            </label>
            <input
              autoFocus
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="e.g. Background verification"
              style={{
                display: "block",
                width: "100%",
                marginTop: "6px",
                fontSize: "13px",
                padding: "8px 10px",
                border: `1px solid ${tokens.color.border.strong}`,
                borderRadius: "7px",
                outline: "none",
                color: tokens.color.text.primary,
                boxSizing: "border-box",
              }}
            />
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: "20px",
          py: "14px",
          borderTop: `1px solid ${tokens.color.border.default}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          style={{
            fontSize: "13px",
            fontWeight: 500,
            color: tokens.color.text.secondary,
            backgroundColor: tokens.color.surface.subtle,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: "7px",
            padding: "8px 18px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={!isReady}
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: tokens.color.surface.default,
            backgroundColor: isReady ? tokens.color.text.primary : `${tokens.color.text.secondary}`,
            border: "none",
            borderRadius: "7px",
            padding: "8px 18px",
            cursor: isReady ? "pointer" : "default",
          }}
        >
          Create Task
        </button>
      </Box>
    </Drawer>
  );
};

export default NewTaskSheet;
